import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { KeyRound, MailCheck, Loader2 } from 'lucide-react';
import api from '@/lib/axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export default function PasswordReset() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-8">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8">
          {token ? <ResetForm token={token} /> : <RequestForm />}
          <div className="mt-6 text-center">
            <Link to="/login" className="text-sm text-slate-500 hover:text-slate-800 underline" data-testid="back-to-login-link">
              Zurück zur Anmeldung
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function RequestForm() {
  const [identifier, setIdentifier] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/auth/forgot-password', { identifier });
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Anfrage fehlgeschlagen');
    } finally { setLoading(false); }
  };

  if (sent) {
    return (
      <div className="text-center" data-testid="forgot-password-sent">
        <div className="mx-auto w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
          <MailCheck className="w-6 h-6 text-emerald-600" />
        </div>
        <h1 className="text-xl font-bold text-slate-900 mb-2">E-Mail unterwegs</h1>
        <p className="text-sm text-slate-500">
          Falls ein Konto mit diesen Daten existiert, wurde eine E-Mail mit dem Reset-Link gesendet
          (1 Stunde gültig). Prüfen Sie ggf. auch den Spam-Ordner.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="text-center mb-6">
        <div className="mx-auto w-12 h-12 rounded-full bg-brand-red/10 flex items-center justify-center mb-4">
          <KeyRound className="w-6 h-6 text-brand-red" />
        </div>
        <h1 className="text-xl font-bold text-slate-900">Passwort vergessen</h1>
        <p className="text-sm text-slate-500 mt-1">
          Geben Sie Ihren Benutzernamen oder Ihre E-Mail-Adresse ein.
        </p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4" data-testid="forgot-password-form">
        <div className="space-y-1.5">
          <Label htmlFor="identifier">Benutzername oder E-Mail</Label>
          <Input id="identifier" value={identifier} onChange={(e) => setIdentifier(e.target.value)}
                 required className="h-10" data-testid="forgot-identifier-input" />
        </div>
        <Button type="submit" disabled={loading}
                className="w-full bg-slate-900 text-white hover:bg-slate-800 h-10"
                data-testid="forgot-password-submit">
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
          Reset-Link anfordern
        </Button>
      </form>
    </>
  );
}

function ResetForm({ token }) {
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (next.length < 8) return toast.error('Neues Passwort muss mindestens 8 Zeichen lang sein');
    if (next !== repeat) return toast.error('Passwörter stimmen nicht überein');
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, new_password: next });
      toast.success('Passwort zurückgesetzt – bitte anmelden');
      navigate('/login');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Zurücksetzen fehlgeschlagen');
    } finally { setLoading(false); }
  };

  return (
    <>
      <div className="text-center mb-6">
        <div className="mx-auto w-12 h-12 rounded-full bg-brand-red/10 flex items-center justify-center mb-4">
          <KeyRound className="w-6 h-6 text-brand-red" />
        </div>
        <h1 className="text-xl font-bold text-slate-900">Neues Passwort setzen</h1>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-password-form">
        <div className="space-y-1.5">
          <Label htmlFor="next">Neues Passwort <span className="text-xs text-slate-400 font-normal">(min. 8 Zeichen)</span></Label>
          <Input id="next" type="password" value={next} onChange={(e) => setNext(e.target.value)}
                 required className="h-10" data-testid="reset-new-password-input" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="repeat">Neues Passwort wiederholen</Label>
          <Input id="repeat" type="password" value={repeat} onChange={(e) => setRepeat(e.target.value)}
                 required className="h-10" data-testid="reset-repeat-password-input" />
        </div>
        <Button type="submit" disabled={loading}
                className="w-full bg-slate-900 text-white hover:bg-slate-800 h-10"
                data-testid="reset-password-submit">
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
          Passwort zurücksetzen
        </Button>
      </form>
    </>
  );
}
